import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useIntl } from "react-intl";
import { Stack, Typography, Box, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GoogleIcon from "@mui/icons-material/Google";

import { ButtonContained } from "components/button/ButtonContained";
import { ButtonOutlined } from "components/button/ButtonOutlined";
import { PlatformInstructions } from "./PlatformInstructions";
import OutlookIcon from "assets/img/outlook.png";

export const CalendarSubscribe = () => {
	const [searchParams] = useSearchParams();
	const { enqueueSnackbar } = useSnackbar();
	const intl = useIntl();

	const token = searchParams.get("token");
	const [isCopied, setIsCopied] = useState(false);

	const feedUrl = `${window.location.origin}/api/employees/event/calendar.ics?token=${token}`;
	const webcalUrl = feedUrl.replace(/^https?:\/\//, "webcal://");

	const handleOpenInCalendar = () => {
		window.location.href = webcalUrl;
	};

	const handleOpenInGoogle = () => {
		const googleUrl = `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(feedUrl)}`;
		window.open(googleUrl, "_blank");
	};

	const handleOpenInOutlook = () => {
		const calendarName = intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.calendar_name" });
		const outlookUrl = `https://outlook.live.com/owa?path=/calendar/action/compose&rru=addsubscription&url=${encodeURIComponent(
			feedUrl,
		)}&name=${encodeURIComponent(calendarName)}`;
		window.open(outlookUrl, "_blank");
	};

	const handleCopyUrl = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(feedUrl);
			setIsCopied(true);
			enqueueSnackbar(intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.snackbar.url_copied" }), {
				variant: "success",
			});
			setTimeout(() => setIsCopied(false), 3000);
		} catch (error) {
			enqueueSnackbar(intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.snackbar.copy_failed" }), {
				variant: "error",
			});
		}
	}, [feedUrl, enqueueSnackbar, intl]);

	if (!token) {
		return (
			<Stack alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="grey.50" p={2}>
				<Box
					maxWidth="md"
					width="100%"
					bgcolor="common.white"
					borderRadius="0.8rem"
					p={{ xs: 3, sm: 4 }}
					boxShadow={1}
				>
					<Typography variant="h5" color="error" textAlign="center">
						{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.error.invalid_token" })}
					</Typography>
				</Box>
			</Stack>
		);
	}

	return (
		<Stack
			id="CalendarSubscribe"
			alignItems="center"
			justifyContent="center"
			minHeight="100vh"
			bgcolor="grey.50"
			p={{ xs: 2, sm: 3 }}
		>
			<Helmet>
				<title>{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.page.title" })}</title>
			</Helmet>

			<Box
				maxWidth="md"
				width="100%"
				bgcolor="common.white"
				borderRadius={{ xs: "0.8rem", sm: "1.2rem" }}
				p={{ xs: 6, sm: 8, md: 10 }}
				boxShadow={2}
			>
				<Stack spacing={{ xs: 6, sm: 8 }}>
					<Stack spacing={4} alignItems="center" textAlign="center">
						<CalendarTodayIcon
							sx={{
								fontSize: { xs: "3.2rem", sm: "4rem" },
								color: "primary.main",
							}}
						/>
						<Typography variant="h4" fontWeight={700} fontSize="2.4rem">
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.title" })}
						</Typography>
						<Typography fontSize="1.6rem" color="text.secondary" maxWidth="50rem">
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.description" })}
						</Typography>
					</Stack>

					<Divider />

					<Stack spacing={4}>
						<ButtonContained
							fullWidth
							size="large"
							onClick={handleOpenInCalendar}
							startIcon={<CalendarTodayIcon />}
							sx={{
								py: { xs: 3, sm: 6 },
								fontSize: "1.6rem",
							}}
							data-useremunit="true"
						>
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.button.open_in_calendar" })}
						</ButtonContained>

						<ButtonContained
							fullWidth
							size="large"
							onClick={handleOpenInGoogle}
							startIcon={<GoogleIcon />}
							sx={{
								py: { xs: 3, sm: 6 },
								fontSize: "1.6rem",
								backgroundColor: "#4285F4",
								"&:hover": {
									backgroundColor: "#357ae8",
								},
							}}
							data-useremunit="true"
						>
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.button.open_in_google" })}
						</ButtonContained>

						<ButtonContained
							fullWidth
							size="large"
							onClick={handleOpenInOutlook}
							startIcon={
								<Box
									component="img"
									src={OutlookIcon}
									alt="Outlook"
									sx={{ width: "2rem", height: "2rem" }}
								/>
							}
							sx={{
								py: { xs: 3, sm: 6 },
								fontSize: "1.6rem",
								backgroundColor: "#0078D4",
								"&:hover": {
									backgroundColor: "#106ebe",
								},
							}}
							data-useremunit="true"
						>
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.button.open_in_outlook" })}
						</ButtonContained>

						<ButtonOutlined
							fullWidth
							size="large"
							onClick={handleCopyUrl}
							startIcon={<ContentCopyIcon />}
							sx={{
								py: { xs: 3, sm: 6 },
								fontSize: "1.6rem",
							}}
							data-useremunit="true"
						>
							{isCopied
								? intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.button.copied" })
								: intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.button.copy_url" })}
						</ButtonOutlined>
					</Stack>

					<Divider />

					<Stack spacing={4}>
						<Typography variant="h6" fontWeight={600} fontSize="1.6rem">
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.section.instructions" })}
						</Typography>
						<Typography fontSize="1.6rem" color="text.secondary">
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.instructions.description" })}
						</Typography>
						<PlatformInstructions feedUrl={feedUrl} />
					</Stack>

					<Box bgcolor="grey.50" p={{ xs: 4, sm: 6 }} borderRadius="0.8rem" mt={4}>
						<Typography fontSize="1.4rem" color="text.secondary" textAlign="center">
							{intl.formatMessage({ id: "CALENDAR_SUBSCRIBE.tip.sync" })}
						</Typography>
					</Box>
				</Stack>
			</Box>
		</Stack>
	);
};
